import * as React from 'react'

import { Text, Tooltip, useToggle } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import formatCurrency from 'modules/core/lib/formatCurrency'
import { currencyToNumber, isValidAmount } from 'modules/core/lib/formatCurrencyUtils'
import i18n from 'modules/core/lib/i18n'

import taxonomies from 'modules/contracts/store/collections/taxonomies'
import type { SupplementType } from 'modules/payroll_bundle/components/common/helper/worksheet'
import Supplement from 'modules/payroll_bundle/store/models/Supplement'

const formatAmount = (amount: string, units: string, legalEntityId?: number) => {
  if (!amount) {
    return '--'
  }

  if (units === Supplement.units.money) {
    // If legalEntityId is falsy, the company's currency will be used,
    // otherwise the legal entity's currency
    return formatCurrency(currencyToNumber(amount) * 100, '', null, legalEntityId)
  }

  // Assumes units is Supplement.units.units
  return i18n.tp('compensation.unit.units', { count: Number(amount) })
}

const Amount = ({
  supplement,
  supplementType,
  rowHasEmployeeAssigned,
}: {
  supplement: Supplement
  supplementType: SupplementType
  rowHasEmployeeAssigned: boolean
}) => {
  const [open, toggle] = useToggle(false)
  // Note that if the input is just whitespace, it's converted to the empty string
  const amount = supplement.attributes[supplementType.id]?.trim()
  let formattedAmount
  let hasError
  let label
  // Uses the fact that both undefined and the empty string are falsy
  if (!amount || isValidAmount(amount)) {
    formattedAmount = formatAmount(
      amount,
      supplementType.units,
      supplement.attributes.legal_entity_id
    )

    if (rowHasEmployeeAssigned && amount) {
      const taxonomy = taxonomies.get(
        supplementType.legalEntityIdToTaxonomyId[supplement.attributes.legal_entity_id]
      )

      hasError = !taxonomy
      label = taxonomy
        ? taxonomy.attributes.name
        : i18n.t('payroll_bundle.import_supplements.review_and_import.missing_data_mapping')
    }
  } else {
    formattedAmount = amount
    hasError = true
    label = i18n.t('payroll_bundle.import_supplements.review_and_import.invalid_value')
  }

  const color = hasError ? 'radicalRed1200' : undefined

  if (label) {
    return (
      <Tooltip.Alt
        attachment="bottom center"
        targetAttachment="top center"
        toggle={toggle}
        open={open}
        label={label}
        backgroundColor={hasError ? 'radicalRed1000' : undefined}
      >
        <Text.Normal color={color}>{formattedAmount}</Text.Normal>
      </Tooltip.Alt>
    )
  }

  return <Text.Normal color={color}>{formattedAmount}</Text.Normal>
}

export default observer(Amount)
